import { iterate } from "../process/iterate.ts";
import type { PathBBox } from "../interface.ts";
import type { LSegment, MSegment, PathArray } from "../types.ts";
import { getLineBBox } from "../math/lineTools.ts";
import { getArcBBox } from "../math/arcTools.ts";
import { getCubicBBox } from "../math/cubicTools.ts";
import { getQuadBBox } from "../math/quadTools.ts";
import { parsePathString } from "../parser/parsePathString.ts";
import { absolutizeSegment } from "../process/absolutizeSegment.ts";

/**
 * Calculates the bounding box of a path.
 *
 * @param pathInput - The path string or PathArray
 * @returns An object with width, height, x, y, x2, y2, cx, cy, cz properties
 *
 * @example
 * ```ts
 * getPathBBox('M0 0L100 0L100 100L0 100Z')
 * // => { x: 0, y: 0, width: 100, height: 100, x2: 100, y2: 100, cx: 50, cy: 50, cz: 150 }
 * ```
 */
export const getPathBBox = <T extends PathArray>(
  pathInput: T | string,
): {
  x: number;
  y: number;
  width: number;
  height: number;
  x2: number;
  y2: number;
  cx: number;
  cy: number;
  cz: number;
} => {
  if (!pathInput) {
    return {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      x2: 0,
      y2: 0,
      cx: 0,
      cy: 0,
      cz: 0,
    };
  }

  const path = parsePathString(pathInput);
  let pathCommand = "M";
  let mx = 0;
  let my = 0;
  const { max, min } = Math;
  let xMin = Infinity;
  let yMin = Infinity;
  let xMax = -Infinity;
  let yMax = -Infinity;
  let minX = 0;
  let minY = 0;
  let maxX = 0;
  let maxY = 0;
  let paramX1 = 0;
  let paramY1 = 0;
  let paramX2 = 0;
  let paramY2 = 0;
  // keep null check #64
  let paramQX: number | null = null;
  let paramQY: number | null = null;

  iterate(path, (seg, index, lastX, lastY) => {
    [pathCommand] = seg;
    const commandCode = pathCommand.charCodeAt(0);
    const isRelative = commandCode >= 97; // a-z
    const absCommand = (isRelative
      ? String.fromCharCode(commandCode - 32)
      : pathCommand) as typeof pathCommand;
    const absoluteSegment = isRelative
      ? absolutizeSegment(seg, index, lastX, lastY)
      : seg;

    const normalSegment = absCommand === "V"
      ? (["L", lastX, absoluteSegment[1]] as LSegment)
      : absCommand === "H"
      ? (["L", absoluteSegment[1], lastY] as LSegment)
      : absoluteSegment;

    [pathCommand] = normalSegment;

    if (absCommand !== "T" && absCommand !== "Q") {
      // keep null check #64
      paramQX = null;
      paramQY = null;
    }

    // this segment is always ZERO
    /* istanbul ignore else @preserve */
    if (pathCommand === "M") {
      [, mx, my] = normalSegment as MSegment;
      minX = mx;
      minY = my;
      maxX = mx;
      maxY = my;
    } else if (pathCommand === "L") {
      [minX, minY, maxX, maxY] = getLineBBox(
        lastX,
        lastY,
        normalSegment[1] as number,
        normalSegment[2] as number,
      );
    } else if (pathCommand === "A") {
      [minX, minY, maxX, maxY] = getArcBBox(
        lastX,
        lastY,
        normalSegment[1] as number,
        normalSegment[2] as number,
        normalSegment[3] as number,
        normalSegment[4] as number,
        normalSegment[5] as number,
        normalSegment[6] as number,
        normalSegment[7] as number,
      );
    } else if (pathCommand === "S") {
      const cp1x = paramX1 * 2 - paramX2;
      const cp1y = paramY1 * 2 - paramY2;

      [minX, minY, maxX, maxY] = getCubicBBox(
        lastX,
        lastY,
        cp1x,
        cp1y,
        normalSegment[1] as number,
        normalSegment[2] as number,
        normalSegment[3] as number,
        normalSegment[4] as number,
      );
    } else if (pathCommand === "C") {
      [minX, minY, maxX, maxY] = getCubicBBox(
        lastX,
        lastY,
        normalSegment[1] as number,
        normalSegment[2] as number,
        normalSegment[3] as number,
        normalSegment[4] as number,
        normalSegment[5] as number,
        normalSegment[6] as number,
      );
    } else if (pathCommand === "T") {
      // keep null check #64
      paramQX = paramQX === null ? lastX : paramX1 * 2 - paramQX;
      paramQY = paramQY === null ? lastY : paramY1 * 2 - paramQY;
      [minX, minY, maxX, maxY] = getQuadBBox(
        lastX,
        lastY,
        paramQX,
        paramQY,
        normalSegment[1] as number,
        normalSegment[2] as number,
      );
    } else if (pathCommand === "Q") {
      paramQX = normalSegment[1] as number;
      paramQY = normalSegment[2] as number;
      [minX, minY, maxX, maxY] = getQuadBBox(
        lastX,
        lastY,
        normalSegment[1] as number,
        normalSegment[2] as number,
        normalSegment[3] as number,
        normalSegment[4] as number,
      );
    } else if (pathCommand === "Z") {
      [minX, minY, maxX, maxY] = getLineBBox(lastX, lastY, mx, my);
    }
    xMin = min(minX, xMin);
    yMin = min(minY, yMin);
    xMax = max(maxX, xMax);
    yMax = max(maxY, yMax);

    // update params
    if (pathCommand === "Z") {
      paramX1 = mx;
      paramY1 = my;
    } else {
      const segLen = normalSegment.length;
      paramX1 = normalSegment[segLen - 2] as number;
      paramY1 = normalSegment[segLen - 1] as number;
    }
    if (pathCommand === "C") {
      paramX2 = normalSegment[3] as number;
      paramY2 = normalSegment[4] as number;
    } else if (pathCommand === "S") {
      paramX2 = normalSegment[1] as number;
      paramY2 = normalSegment[2] as number;
    } else {
      paramX2 = paramX1;
      paramY2 = paramY1;
    }
  });

  const width = xMax - xMin;
  const height = yMax - yMin;

  return {
    width,
    height,
    x: xMin,
    y: yMin,
    x2: xMax,
    y2: yMax,
    cx: xMin + width / 2,
    cy: yMin + height / 2,
    // an estimated guess
    cz: Math.max(width, height) + Math.min(width, height) / 2,
  } satisfies PathBBox;
};
